import React, { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { API } from "@/lib/constants";
import { Bell, RefreshCw, Mail, Phone, User } from "lucide-react";

const EscalationDashboard = () => {
  const [escalations, setEscalations] = useState([]);
  const [stats, setStats] = useState({ pending: 0, in_progress: 0, resolved: 0, total: 0 });
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);

  const fetchData = async () => {
    try {
      const [escRes, statsRes] = await Promise.all([
        fetch(API + "/escalations?limit=50"),
        fetch(API + "/escalations/stats")
      ]);
      const escData = await escRes.json();
      const statsData = await statsRes.json();
      setEscalations(escData.escalations || []);
      setStats(statsData);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    const wsUrl = API.replace("https://", "wss://").replace("http://", "ws://") + "/escalations/ws";
    
    const connect = () => {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      
      ws.onopen = () => setConnected(true);
      ws.onclose = () => {
        setConnected(false);
        setTimeout(connect, 3000);
      };
      ws.onmessage = (e) => {
        const d = JSON.parse(e.data);
        if (d.type === "new_escalation") {
          setEscalations(prev => [d.escalation, ...prev]);
          fetchData();
        } else if (d.type === "status_update") {
          setEscalations(prev => prev.map(x => x.id === d.escalation_id ? {...x, status: d.status} : x));
          fetchData(); // Refresh stats after status update
        } else if (d.type === "queue_sync") {
          setEscalations(d.escalations || []);
          fetchData(); // Refresh stats after queue sync
        }
      };
    };
    
    connect();
    fetchData();
    
    return () => wsRef.current?.close();
  }, []);

  const updateStatus = (id, status) => {
    if (wsRef.current?.readyState === 1) {
      wsRef.current.send(JSON.stringify({ type: "update_status", escalation_id: id, status, handler: "Admin" }));
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-20 bg-black" data-testid="escalation-dashboard">
      <div className="container-custom">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Bell className="w-8 h-8 text-[#00FF85]" />
            Live Escalations
          </h1>
          <div className="flex gap-4 items-center">
            <span className={connected ? "text-green-400" : "text-red-400"}>
              {connected ? "● Connected" : "● Disconnected"}
            </span>
            <Button variant="outline" onClick={fetchData} className="border-white/20">
              <RefreshCw className="w-4 h-4 mr-2" />Refresh
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-4 gap-4 mb-8">
          <Card className="bg-red-500/10 border-red-500/30">
            <CardContent className="pt-4">
              <p className="text-3xl font-bold text-red-400">{stats.pending}</p>
              <p className="text-sm text-gray-400">Pending</p>
            </CardContent>
          </Card>
          <Card className="bg-yellow-500/10 border-yellow-500/30">
            <CardContent className="pt-4">
              <p className="text-3xl font-bold text-yellow-400">{stats.in_progress}</p>
              <p className="text-sm text-gray-400">In Progress</p>
            </CardContent>
          </Card>
          <Card className="bg-green-500/10 border-green-500/30">
            <CardContent className="pt-4">
              <p className="text-3xl font-bold text-green-400">{stats.resolved}</p>
              <p className="text-sm text-gray-400">Resolved</p>
            </CardContent>
          </Card>
          <Card className="bg-blue-500/10 border-blue-500/30">
            <CardContent className="pt-4">
              <p className="text-3xl font-bold text-blue-400">{stats.total}</p>
              <p className="text-sm text-gray-400">Total</p>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-4">
          {escalations.length === 0 ? (
            <Card className="bg-[#111] border-white/10">
              <CardContent className="py-12 text-center text-gray-400">
                No escalations yet. New requests appear here in real-time.
              </CardContent>
            </Card>
          ) : (
            escalations.map((esc) => (
              <Card key={esc.id} className={`bg-[#111] border-white/10 ${esc.status === "pending" ? "border-l-4 border-l-red-500" : ""}`}>
                <CardContent className="py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                        <User className="w-5 h-5 text-blue-400" />
                      </div>
                      <div>
                        <p className="text-white font-medium">{esc.firstName} {esc.lastName}</p>
                        <div className="flex gap-4 text-sm text-gray-400">
                          <a href={`mailto:${esc.email}`} className="flex items-center gap-1 hover:text-[#00FF85]">
                            <Mail className="w-3 h-3" />{esc.email}
                          </a>
                          {esc.phone && (
                            <a href={`tel:${esc.phone}`} className="flex items-center gap-1 hover:text-[#00FF85]">
                              <Phone className="w-3 h-3" />{esc.phone}
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`px-3 py-1 rounded-full text-sm ${
                        esc.status === "pending" ? "bg-red-500/20 text-red-400" :
                        esc.status === "in_progress" ? "bg-yellow-500/20 text-yellow-400" :
                        "bg-green-500/20 text-green-400"
                      }`}>
                        {esc.status === "in_progress" ? "In Progress" : esc.status}
                      </span>
                      {esc.status === "pending" && (
                        <Button size="sm" onClick={() => updateStatus(esc.id, "in_progress")} className="bg-yellow-500 text-black">
                          Take Over
                        </Button>
                      )}
                      {esc.status === "in_progress" && (
                        <Button size="sm" onClick={() => updateStatus(esc.id, "resolved")} className="bg-green-500 text-black">
                          Resolve
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default EscalationDashboard;
